import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { businessService, customerService } from '../services/firebaseService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'business' | 'customer' | null
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        
        // Determine user role by trying to fetch business or customer data
        try {
          // Try business data first
          const business = await businessService.getBusinessData(user.uid);
          if (business) {
            setBusinessData(business);
            setUserRole('business');
            setCustomerData(null);
          } else {
            // Not a business, try customer data
            const customer = await customerService.getCustomerData(user.uid);
            if (customer) {
              setCustomerData(customer);
              setUserRole('customer');
              setBusinessData(null);
            } else {
              // No data found, likely new customer or anonymous
              setUserRole(user.isAnonymous ? 'customer' : null);
              setBusinessData(null);
              setCustomerData(null);
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setBusinessData(null);
          setCustomerData(null);
          setUserRole(user.isAnonymous ? 'customer' : null);
        }
      } else {
        setUser(null);
        setBusinessData(null);
        setCustomerData(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = {
    user,
    businessData,
    customerData,
    userRole,
    loading,
    isAuthenticated: !!user,
    isAnonymous: user?.isAnonymous || false,
    isBusiness: userRole === 'business',
    isCustomer: userRole === 'customer',
    refreshBusinessData: async () => {
      if (user) {
        try {
          const business = await businessService.getBusinessData(user.uid);
          setBusinessData(business);
        } catch (error) {
          console.error('Error refreshing business data:', error);
        }
      }
    },
    refreshCustomerData: async () => {
      if (user) {
        try {
          const customer = await customerService.getCustomerData(user.uid);
          setCustomerData(customer);
        } catch (error) {
          console.error('Error refreshing customer data:', error);
        }
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
